import React, { useMemo, useState } from 'react';
import {
  DescriptionText,
  HorizontalStack,
  PaneFooter,
  PaneHeader,
  PaneSection,
  Surface,
  VerticalStack,
  WizardWorkspace,
} from '@workspace/silk-ui';
import { CheckCircle2, Mail, Users } from 'lucide-react';

export interface NotificationRecipientGroupMemberFormState {
  id?: string;
  email: string;
  display_name: string;
  enabled: boolean;
}

export interface NotificationRecipientGroupEditorFormState {
  id?: string;
  group_key: string;
  display_name: string;
  enabled: boolean;
  members: NotificationRecipientGroupMemberFormState[];
}

interface NotificationRecipientGroupEditorWizardProps {
  isOpen: boolean;
  form: NotificationRecipientGroupEditorFormState;
  setForm: React.Dispatch<React.SetStateAction<NotificationRecipientGroupEditorFormState>>;
  saving: boolean;
  onSave: () => Promise<void>;
  onClose: () => void;
}

const inputCls =
  'h-12 w-full rounded-lg border border-border/60 bg-background px-4 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5';

function FormField({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">{label}</label>
      {children}
      {hint ? <p className="text-[10px] font-medium text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function NotificationRecipientGroupEditorWizard({
  isOpen,
  form,
  setForm,
  saving,
  onSave,
  onClose,
}: NotificationRecipientGroupEditorWizardProps) {
  const [activeTabId, setActiveTabId] = useState('identity');
  const mode = form.id ? 'edit' : 'create';
  const summaryItems = useMemo(
    () => [
      { label: 'Group key', value: form.group_key || 'Set a key', complete: Boolean(form.group_key.trim()) },
      { label: 'Name', value: form.display_name || 'Name the group', complete: Boolean(form.display_name.trim()) },
      { label: 'Members', value: `${form.members.length} configured`, complete: form.members.length > 0 },
      { label: 'Status', value: form.enabled ? 'Enabled' : 'Disabled', complete: true },
    ],
    [form.display_name, form.enabled, form.group_key, form.members.length],
  );

  if (!isOpen) return null;

  return (
    <WizardWorkspace
      isOpen={true}
      title={mode === 'edit' ? 'Edit Recipient Group' : 'New Recipient Group'}
      subtitle="Create reusable operational distribution lists for notification routes."
      tabs={[
        { id: 'identity', label: 'Identity', icon: <Users className="h-4 w-4" /> },
        { id: 'members', label: 'Members', icon: <Mail className="h-4 w-4" /> },
        { id: 'review', label: 'Review', icon: <CheckCircle2 className="h-4 w-4" /> },
      ]}
      activeTabId={activeTabId}
      onTabChange={setActiveTabId}
      onSave={onSave}
      onClose={onClose}
      isSaving={saving}
      saveLabel={mode === 'edit' ? 'Save Group' : 'Create Group'}
      summaryItems={summaryItems}
    >
      {activeTabId === 'identity' ? (
        <VerticalStack gap={6}>
          <PaneHeader title="Recipient Group" subtitle="Define the reusable tenant-owned recipient group identity." />
          <div className="px-6 pb-8">
            <PaneSection label="Group metadata" icon={Users}>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <FormField label="Display name">
                  <input aria-label="Display name" className={inputCls} value={form.display_name} onChange={event => setForm(current => ({ ...current, display_name: event.target.value }))} />
                </FormField>
                <FormField label="Group key" hint="Stable tenant-scoped identifier used by route targets.">
                  <input aria-label="Group key" className={inputCls} value={form.group_key} onChange={event => setForm(current => ({ ...current, group_key: event.target.value }))} />
                </FormField>
                <FormField label="Status">
                  <label className="flex h-12 items-center gap-3 rounded-lg border border-border/60 bg-background px-4">
                    <input aria-label="Enabled" type="checkbox" checked={form.enabled} onChange={event => setForm(current => ({ ...current, enabled: event.target.checked }))} />
                    <span className="text-sm font-medium text-foreground">{form.enabled ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </FormField>
              </div>
            </PaneSection>
          </div>
        </VerticalStack>
      ) : null}

      {activeTabId === 'members' ? (
        <VerticalStack gap={6}>
          <PaneHeader title="Members" subtitle="Add one or more email targets that should receive matching notifications." />
          <div className="px-6 pb-8">
            <PaneSection label="Recipient emails" icon={Mail}>
              <div className="space-y-4">
                {form.members.map((member, index) => (
                  <Surface key={member.id ?? `member-${index}`} className="border-border/60 bg-muted/10 p-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField label="Email">
                        <input aria-label={`Member ${index + 1} email`} className={inputCls} value={member.email} onChange={event => setForm(current => ({ ...current, members: current.members.map((item, itemIndex) => itemIndex === index ? { ...item, email: event.target.value } : item) }))} />
                      </FormField>
                      <FormField label="Display name">
                        <input aria-label={`Member ${index + 1} display name`} className={inputCls} value={member.display_name} onChange={event => setForm(current => ({ ...current, members: current.members.map((item, itemIndex) => itemIndex === index ? { ...item, display_name: event.target.value } : item) }))} />
                      </FormField>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <label className="flex items-center gap-3 text-sm font-medium text-foreground">
                        <input type="checkbox" checked={member.enabled} onChange={event => setForm(current => ({ ...current, members: current.members.map((item, itemIndex) => itemIndex === index ? { ...item, enabled: event.target.checked } : item) }))} />
                        <span>{member.enabled ? 'Enabled' : 'Disabled'}</span>
                      </label>
                      <button type="button" className="text-xs font-bold text-destructive" onClick={() => setForm(current => ({ ...current, members: current.members.filter((_, itemIndex) => itemIndex !== index) }))}>
                        Remove
                      </button>
                    </div>
                  </Surface>
                ))}
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-md border border-border/60 bg-background px-3 py-2 text-xs font-bold text-foreground"
                  onClick={() => setForm(current => ({ ...current, members: [...current.members, { email: '', display_name: '', enabled: true }] }))}
                >
                  Add recipient
                </button>
              </div>
            </PaneSection>
          </div>
        </VerticalStack>
      ) : null}

      {activeTabId === 'review' ? (
        <VerticalStack gap={6}>
          <PaneHeader title="Review" subtitle="Confirm the recipient group before saving it for route targeting." />
          <div className="px-6 pb-8">
            <PaneSection label="Group summary" icon={CheckCircle2}>
              <Surface className="border-border/60 bg-muted/10 p-4">
                <VerticalStack gap={3}>
                  <HorizontalStack className="justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">Group</span>
                    <span className="font-bold text-foreground">{form.display_name || 'Unnamed group'}</span>
                  </HorizontalStack>
                  <HorizontalStack className="justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">Key</span>
                    <span className="font-mono text-foreground">{form.group_key || 'not-set'}</span>
                  </HorizontalStack>
                  <HorizontalStack className="justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">Members</span>
                    <span className="font-bold text-foreground">{form.members.length}</span>
                  </HorizontalStack>
                </VerticalStack>
              </Surface>
            </PaneSection>
            <PaneFooter className="mt-8">
              <DescriptionText>
                Recipient groups are reusable operational targets. Notification routes can fan out to multiple groups.
              </DescriptionText>
            </PaneFooter>
          </div>
        </VerticalStack>
      ) : null}
    </WizardWorkspace>
  );
}
